/**
 * Constantes de endpoints de la API
 */

export const ENDPOINTS = {
  // Auth
  AUTH_LOGIN: '/api/auth/login',
  AUTH_REGISTER: '/api/auth/register',
  AUTH_REFRESH: '/api/auth/refresh',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_ME: '/api/auth/me',
  AUTH_ONBOARDING: '/api/auth/me/onboarding',
  AUTH_FORGOT_PASSWORD: '/api/auth/forgot-password',
  AUTH_RESET_PASSWORD: '/api/auth/reset-password',
  // Canjea el token de selección (o el de sesión) por una sesión en esa empresa.
  AUTH_PORTAL_EXCHANGE: (slug: string) => `/api/auth/portal/${slug}`,
  // Los portales de quien está conectado — sobrevive a que el token de
  // selección del ingreso ya haya vencido (dura 10 minutos).
  AUTH_PORTALS: '/api/auth/portals',

  // Tenants
  TENANTS: '/api/tenants',
  TENANT_BY_ID: (id: string) => `/api/tenants/${id}`,

  // Users
  USERS: '/api/users',
  USER_BY_ID: (id: string) => `/api/users/${id}`,
  USER_PROFILE: '/api/users/profile',
  // Restablecer la contraseña de OTRA persona, siendo admin. No existía: su
  // ausencia obligó a un UPDATE a mano en la base de producción (vaultProgress/130).
  USER_RESET_PASSWORD: (id: string) => `/api/users/${id}/reset-password`,

  // Pricing Models
  PRICING_MODELS: '/api/pricing-models',
  PRICING_MODEL_BY_ID: (id: string) => `/api/pricing-models/${id}`,
  VALIDATE_FORMULA: '/api/pricing-models/validate-formula',

  // Master Data - Currencies
  CURRENCIES: '/api/master-data/currencies',
  CURRENCY_BY_ID: (id: string) => `/api/master-data/currencies/${id}`,
  CURRENCY_SYNC_RATES: '/api/master-data/currencies/sync-rates',
  CURRENCY_RATE_HISTORY: '/api/master-data/currencies/rate-history',
  CURRENCY_EXCHANGE_SUMMARY: '/api/master-data/currencies/exchange-summary',

  // Master Data - Exchange Rates (tenant-scoped, por fecha)
  EXCHANGE_RATES: '/api/master-data/exchange-rates',
  EXCHANGE_RATE_BY_ID: (id: string) => `/api/master-data/exchange-rates/${id}`,
  EXCHANGE_RATE_LOOKUP: '/api/master-data/exchange-rates/lookup',

  // Master Data - LOB (Lines of Business)
  LOBS: '/api/master-data/lobs',
  LOB_BY_ID: (id: string) => `/api/master-data/lobs/${id}`,

  // Master Data - Rate Cards
  RATE_CARDS: '/api/master-data/rate-cards',
  RATE_CARD_BY_ID: (id: string) => `/api/master-data/rate-cards/${id}`,

  // Master Data - Countries
  COUNTRIES: '/api/master-data/countries',
  COUNTRY_BY_ID: (id: string) => `/api/master-data/countries/${id}`,

  // Master Data - Quote Types
  QUOTE_TYPES: '/api/quote-types',
  QUOTE_TYPE_BY_ID: (id: string) => `/api/quote-types/${id}`,
  QUOTE_TYPE_RESOLVE_PRICES: '/api/quote-types/resolve-prices',
  QUOTE_TYPE_MATERIAL_PRICES: (id: string) => `/api/quote-types/${id}/material-prices`,
  QUOTE_TYPE_APPROVED_QUOTES: (id: string) => `/api/quote-types/${id}/approved-quotes`,

  // Master Data - Taxes
  TAX_CONFIGS: '/api/master-data/taxes',
  TAX_BY_ID: (id: string) => `/api/master-data/taxes/${id}`,

  // Master Data - Import
  CURRENCIES_IMPORT: '/api/master-data/currencies/import',
  LOBS_IMPORT: '/api/master-data/lobs/import',
  TAX_CONFIGS_IMPORT: '/api/master-data/taxes/import',
  COUNTRIES_IMPORT: '/api/master-data/countries/import',
  MATERIALS_IMPORT: '/api/materials/import',

  // Materials
  MATERIALS: '/api/materials',
  MATERIAL_BY_ID: (id: string) => `/api/materials/${id}`,
  MATERIAL_TARIFF: (id: string) => `/api/materials/${id}/tariff`,
  MATERIAL_TARIFF_HISTORY: (id: string) => `/api/materials/${id}/tariff-history`,

  // Price Lists
  PRICE_LISTS: '/api/price-lists',
  PRICE_LIST_BY_ID: (id: string) => `/api/price-lists/${id}`,
  PRICE_LIST_ENTRIES: (id: string) => `/api/price-lists/${id}/entries`,
  PRICE_LIST_ENTRY_BY_ID: (id: string, entryId: string) =>
    `/api/price-lists/${id}/entries/${entryId}`,

  // Material Activities
  MATERIAL_ACTIVITIES: (id: string) => `/api/materials/${id}/activities`,
  MATERIAL_ACTIVITY_BY_ID: (id: string, activityId: string) =>
    `/api/materials/${id}/activities/${activityId}`,

  // Material Image
  MATERIAL_IMAGE: (id: string) => `/api/materials/${id}/image`,

  // Quotes
  QUOTES: '/api/quotes',
  QUOTE_BY_ID: (id: string) => `/api/quotes/${id}`,
  QUOTE_CALCULATE: '/api/quotes/calculate',
  QUOTE_SUBMIT: (id: string) => `/api/quotes/${id}/submit`,

  // Quote Templates
  QUOTE_TEMPLATES: '/api/quotes/templates',
  QUOTE_SAVE_AS_TEMPLATE: (id: string) => `/api/quotes/${id}/save-as-template`,
  QUOTE_FROM_TEMPLATE: (id: string) => `/api/quotes/from-template/${id}`,

  // Quote Tags
  QUOTE_TAGS: '/api/quotes/tags',
  QUOTE_TAG_BY_ID: (id: string) => `/api/quotes/tags/${id}`,
  QUOTE_ATTACH_TAGS: (quoteId: string) => `/api/quotes/${quoteId}/tags`,
  QUOTE_DETACH_TAG: (quoteId: string, tagId: string) => `/api/quotes/${quoteId}/tags/${tagId}`,

  // Quote Comments
  QUOTE_COMMENTS: (quoteId: string) => `/api/quotes/${quoteId}/comments`,
  QUOTE_COMMENT_BY_ID: (quoteId: string, commentId: string) =>
    `/api/quotes/${quoteId}/comments/${commentId}`,

  // Approvals
  APPROVALS: '/api/approvals',
  APPROVAL_BY_ID: (id: string) => `/api/approvals/${id}`,
  APPROVAL_APPROVE: (id: string) => `/api/approvals/${id}/approve`,
  APPROVAL_REJECT: (id: string) => `/api/approvals/${id}/reject`,
  APPROVAL_ADJUST: (id: string) => `/api/approvals/${id}/adjust`,

  // Documents
  DOCUMENTS: '/api/documents',
  DOCUMENT_BY_ID: (id: string) => `/api/documents/${id}`,
  GENERATE_PDF: (quoteId: string) => `/api/documents/generate/${quoteId}`,
  FREEZE_DOCUMENT: (id: string) => `/api/documents/${id}/freeze`,

  // Dashboard
  DASHBOARD_STATS: '/api/dashboard/stats',
  DASHBOARD_RECENT: '/api/dashboard/recent',
  DASHBOARD_ACTIVITY: '/api/dashboard/activity',

  // Notifications
  NOTIFICATIONS: '/api/notifications',
  NOTIFICATIONS_UNREAD_COUNT: '/api/notifications/unread-count',
  NOTIFICATION_READ: (id: string) => `/api/notifications/${id}/read`,
  NOTIFICATIONS_READ_ALL: '/api/notifications/read-all',
  NOTIFICATION_DELETE: (id: string) => `/api/notifications/${id}`,

  // Customers
  CUSTOMERS: '/api/customers',
  CUSTOMER_BY_ID: (id: string) => `/api/customers/${id}`,
  CUSTOMER_CONTACTS: (id: string) => `/api/customers/${id}/contacts`,
  CUSTOMER_CONTACT_BY_ID: (customerId: string, contactId: string) =>
    `/api/customers/${customerId}/contacts/${contactId}`,
  CUSTOMERS_IMPORT: '/api/customers/import',
  CUSTOMERS_UNIFIED: '/api/customers/unified',

  // Analytics
  ANALYTICS_OVERVIEW: '/api/analytics/overview',
  ANALYTICS_MARGINS: '/api/analytics/margins',
  ANALYTICS_WIN_LOSS: '/api/analytics/win-loss',
  ANALYTICS_DISCOUNT_DEPTH: '/api/analytics/discount-depth',
  ANALYTICS_MODEL_EFFECTIVENESS: '/api/analytics/model-effectiveness',
  ANALYTICS_TRENDS: '/api/analytics/trends',
  ANALYTICS_PRICE_WATERFALL: '/api/analytics/price-waterfall',

  // Sales Training
  SALES_TRAINING_PROFILE: '/api/sales-training/profile',
  SALES_TRAINING_EXAMPLES: '/api/sales-training/examples',
  SALES_TRAINING_EXAMPLE_BY_ID: (id: string) => `/api/sales-training/examples/${id}`,

  // AI Intelligence
  AI_INTELLIGENCE_SIMULATE: '/api/ai-intelligence/simulate',
  AI_INTELLIGENCE_JUSTIFICATION: '/api/ai-intelligence/justification',
  AI_INTELLIGENCE_SUGGESTIONS: '/api/ai-intelligence/suggestions',
  AI_INTELLIGENCE_APPROVAL_SCORE: '/api/ai-intelligence/approval-score',
  AI_INTELLIGENCE_INSIGHTS: '/api/ai-intelligence/insights',
  AI_INTELLIGENCE_MODEL_ANALYSIS: '/api/ai-intelligence/model-analysis',
  AI_INTELLIGENCE_QUOTES_SUMMARY: '/api/ai-intelligence/quotes-summary',
  AI_INTELLIGENCE_MODELS_SUMMARY: '/api/ai-intelligence/models-summary',

  // AI Assistant
  AI_CHAT: '/api/ai-assistant/chat',
  AI_CONVERSATIONS: '/api/ai-assistant/conversations',
  AI_CONVERSATION_BY_ID: (id: string) => `/api/ai-assistant/conversations/${id}`,

  // Permissions
  PERMISSIONS: '/api/permissions',
  PERMISSIONS_MY: '/api/permissions/my-permissions',
  PERMISSIONS_COMPANY_MODULES: '/api/permissions/company-modules',
  PERMISSIONS_BY_ROLE: (role: string) => `/api/permissions/${role}`,
  PERMISSIONS_RESET: (role: string) => `/api/permissions/reset/${role}`,

  // Custom Roles
  CUSTOM_ROLES: '/api/custom-roles',
  CUSTOM_ROLE_BY_ID: (id: string) => `/api/custom-roles/${id}`,
  CUSTOM_ROLE_PERMISSIONS: (id: string) => `/api/custom-roles/${id}/permissions`,
  CUSTOM_ROLE_DUPLICATE: (id: string) => `/api/custom-roles/${id}/duplicate`,

  // Links de invitación (admin)
  INVITATIONS: '/api/invitations',
  INVITATION_BY_ID: (id: string) => `/api/invitations/${id}`,
  // Registro público vía invitación
  PUBLIC_INVITATION: (token: string) => `/api/public/invitations/${token}`,
  PUBLIC_INVITATION_ACCEPT: (token: string) => `/api/public/invitations/${token}/accept`,

  // Dynamic Pricing — E-commerce
  DYNAMIC_PRICING_PRODUCTS: '/api/dynamic-pricing/products',
  DYNAMIC_PRICING_PRODUCT_FILTER_COUNTS: '/api/dynamic-pricing/products/filter-counts',
  DYNAMIC_PRICING_PRODUCTS_EXPORT: '/api/dynamic-pricing/products/export',
  DYNAMIC_PRICING_PRODUCT_BY_ID: (id: string) => `/api/dynamic-pricing/products/${id}`,
  DYNAMIC_PRICING_PRODUCT_CALCULATE: (id: string) =>
    `/api/dynamic-pricing/products/${id}/calculate`,
  DYNAMIC_PRICING_PRODUCT_COMPETITORS: (productId: string) =>
    `/api/dynamic-pricing/products/${productId}/competitor-products`,

  DYNAMIC_PRICING_COMPETITORS: '/api/dynamic-pricing/competitors',
  /** Prender o apagar varios locales de una vez: el último paso del alta de un cliente. */
  DYNAMIC_PRICING_COMPETITORS_BULK_ACTIVE: '/api/dynamic-pricing/competitors/bulk-active',
  /** El buscador de locales sobre el catálogo global: el cliente elige sin pegar URLs. */
  LOCALES_BUSCAR: '/api/locales/buscar',
  LOCALES_CIUDADES: '/api/locales/ciudades',
  /**
   * Cuántos competidores puede seguir el cliente, y con qué frescura se leen a esa
   * cantidad. La cuenta la hace el servidor: los frenos viven en el recolector.
   */
  DYNAMIC_PRICING_COMPETITORS_LIMITE: '/api/dynamic-pricing/competitors/limite',
  /** Inferencia contra TODOS los competidores leidos, de una vez. */
  DYNAMIC_PRICING_CATALOG_INFER_ALL: '/api/dynamic-pricing/catalog/infer-all',
  DYNAMIC_PRICING_COMPETITOR_BY_ID: (id: string) => `/api/dynamic-pricing/competitors/${id}`,
  DYNAMIC_PRICING_COMPETITOR_MONITORING: (id: string) =>
    `/api/dynamic-pricing/competitors/${id}/monitoring`,
  DYNAMIC_PRICING_COMPETITOR_MISSING_ITEMS: (id: string) =>
    `/api/dynamic-pricing/competitors/${id}/missing-items`,
  DYNAMIC_PRICING_COMPETITOR_RESET: (id: string) => `/api/dynamic-pricing/competitors/${id}/reset`,

  DYNAMIC_PRICING_COMPETITOR_PRODUCTS: '/api/dynamic-pricing/competitor-products',
  DYNAMIC_PRICING_COMPETITOR_PRODUCT_BY_ID: (id: string) =>
    `/api/dynamic-pricing/competitor-products/${id}`,
  DYNAMIC_PRICING_COMPETITOR_PRODUCT_PRICE: (id: string) =>
    `/api/dynamic-pricing/competitor-products/${id}/price`,
  /** L1 (vault 274): revisión de vínculos de competencia. */
  LINK_REVIEW_SOSPECHOSOS: '/api/link-review/sospechosos',
  LINK_REVIEW_DECISION: (id: string) => `/api/link-review/vinculos/${id}/decision`,
  LINK_REVIEW_SEGURAS: '/api/link-review/seguras',
  LINK_REVIEW_SEGURAS_CONFIRMAR: '/api/link-review/seguras/confirmar',
  LINK_REVIEW_SEGURA_DESCARTAR: (id: string) => `/api/link-review/seguras/${id}/descartar`,

  DYNAMIC_PRICING_DATA_SOURCES: '/api/dynamic-pricing/data-sources',
  DYNAMIC_PRICING_DATA_SOURCE_BY_ID: (id: string) => `/api/dynamic-pricing/data-sources/${id}`,

  // L5 (vault 278): el modelo único de alertas, con «vista» por persona.
  ALERTAS: '/api/alertas',
  ALERTAS_RESUMEN: '/api/alertas/resumen',
  ALERTAS_VISTA: (id: string) => `/api/alertas/${id}/vista`,
  ALERTAS_VISTAS: '/api/alertas/vistas',

  DYNAMIC_PRICING_LOGS: '/api/dynamic-pricing/logs',
  DYNAMIC_PRICING_STATUS: '/api/dynamic-pricing/status',

  DYNAMIC_PRICING_SCRAPE: '/api/dynamic-pricing/scrape',
  DYNAMIC_PRICING_CALCULATE_ALL: '/api/dynamic-pricing/calculate-all',

  // Bug Report
  BUG_REPORT: '/api/bug-report',

  // Error Tracker (automático)
  ERROR_TRACK: '/api/errors/track',

  // Dynamic Pricing — Catalog Scraping
  DYNAMIC_PRICING_CATALOG_SCRAPER_TYPES: '/api/dynamic-pricing/catalog/scraper-types',
  DYNAMIC_PRICING_CATALOG_SCRAPE: '/api/dynamic-pricing/catalog/scrape',
  DYNAMIC_PRICING_CATALOG_JOBS: '/api/dynamic-pricing/catalog/jobs',
  DYNAMIC_PRICING_CATALOG_JOB_PROGRESS: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/progress`,
  DYNAMIC_PRICING_CATALOG_JOB_CANCEL: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/cancel`,
  DYNAMIC_PRICING_CATALOG_JOB_RESUME: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/resume`,
  DYNAMIC_PRICING_CATALOG_JOB_DELETE: (id: string) => `/api/dynamic-pricing/catalog/jobs/${id}`,
  DYNAMIC_PRICING_CATALOG_ITEMS: '/api/dynamic-pricing/catalog/items',
  DYNAMIC_PRICING_CATALOG_ITEM_MATCH: (id: string) =>
    `/api/dynamic-pricing/catalog/items/${id}/match`,
  DYNAMIC_PRICING_CATALOG_ITEMS_BULK_MATCH: '/api/dynamic-pricing/catalog/items/bulk-match',
  DYNAMIC_PRICING_CATALOG_ITEM_IGNORE: (id: string) =>
    `/api/dynamic-pricing/catalog/items/${id}/ignore`,

  // Carga manual de precios de competencia — spec 199 §3.1 y §4
  CARGA_MANUAL_PLANTILLA: (storeId: string) =>
    `/api/dynamic-pricing/catalog/stores/${storeId}/plantilla`,
  CARGA_MANUAL_SUBIR: (storeId: string) =>
    `/api/dynamic-pricing/catalog/stores/${storeId}/carga-manual`,
  CARGA_MANUAL_ARTICULOS: (storeId: string) =>
    `/api/dynamic-pricing/catalog/stores/${storeId}/articulos`,
  CARGA_MANUAL_PANEL: (jobId: string) =>
    `/api/dynamic-pricing/catalog/jobs/${jobId}/identificacion`,
  CARGA_MANUAL_DECIDIR: (itemId: string) =>
    `/api/dynamic-pricing/catalog/items/${itemId}/identidad`,
  CARGA_MANUAL_BLOQUE: (jobId: string) =>
    `/api/dynamic-pricing/catalog/jobs/${jobId}/identificacion/bloque`,
  CARGA_MANUAL_CERRAR: (jobId: string) =>
    `/api/dynamic-pricing/catalog/jobs/${jobId}/identificacion/cerrar`,

  // Inference / Auto-Match
  DYNAMIC_PRICING_CATALOG_JOB_INFER: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/infer`,
  DYNAMIC_PRICING_CATALOG_JOB_SUGGESTIONS: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/suggestions`,
  DYNAMIC_PRICING_CATALOG_JOB_SUGGESTIONS_GROUPED: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/suggestions-grouped`,
  DYNAMIC_PRICING_CATALOG_JOB_INFERENCE_STATS: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/inference-stats`,
  DYNAMIC_PRICING_CATALOG_JOB_PRODUCT_GROUPS: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/product-groups`,
  /** Cómo va la inferencia mientras corre: la petición que la lanza tarda minutos. */
  DYNAMIC_PRICING_CATALOG_JOB_INFERENCE_PROGRESS: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/inference-progress`,
  /** L2: corrida de la Inferencia en segundo plano. */
  DYNAMIC_PRICING_INFERENCE_RUNS: (jobId: string) =>
    `/api/dynamic-pricing/inference/jobs/${jobId}/runs`,
  DYNAMIC_PRICING_INFERENCE_RUN: (runId: string) => `/api/dynamic-pricing/inference/runs/${runId}`,
  /** Ronda L2-E: la corrida en curso de la tienda de ese catálogo (null si no hay). */
  DYNAMIC_PRICING_INFERENCE_RUN_ACTIVE: (jobId: string) =>
    `/api/dynamic-pricing/inference/jobs/${jobId}/runs/active`,
  /** C4b: buscar a mano un artículo en el catálogo de la tienda desde la Inferencia. */
  DYNAMIC_PRICING_CATALOG_STORE_BUSCAR_ARTICULOS: (storeId: string) =>
    `/api/dynamic-pricing/catalog/stores/${storeId}/buscar-articulos`,
  DYNAMIC_PRICING_CATALOG_JOB_WITHOUT_OPTIONS: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/products-without-options`,
  DYNAMIC_PRICING_CATALOG_JOB_CLEAN_INFERENCE: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/inference`,
  DYNAMIC_PRICING_CATALOG_STORE_CLEAN_INFERENCE: (storeId: string) =>
    `/api/dynamic-pricing/catalog/stores/${storeId}/inference`,
  DYNAMIC_PRICING_CATALOG_JOB_FIX_PRICES: (id: string) =>
    `/api/dynamic-pricing/catalog/jobs/${id}/fix-prices`,
  DYNAMIC_PRICING_CATALOG_SUGGESTIONS: '/api/dynamic-pricing/catalog/suggestions',
  DYNAMIC_PRICING_CATALOG_SUGGESTION_ACCEPT: (id: string) =>
    `/api/dynamic-pricing/catalog/suggestions/${id}/accept`,
  DYNAMIC_PRICING_CATALOG_SUGGESTION_REJECT: (id: string) =>
    `/api/dynamic-pricing/catalog/suggestions/${id}/reject`,

  // Freight Routes
  FREIGHT_ROUTES: '/api/freight-routes',
  FREIGHT_ROUTE_BY_ID: (id: string) => `/api/freight-routes/${id}`,
  FREIGHT_ROUTE_LEGS: (id: string) => `/api/freight-routes/${id}/legs`,
  FREIGHT_ROUTE_LEG_BY_ID: (routeId: string, legId: string) =>
    `/api/freight-routes/${routeId}/legs/${legId}`,
  FREIGHT_ROUTE_LEG_HISTORY: (routeId: string, legId: string) =>
    `/api/freight-routes/${routeId}/legs/${legId}/rate-history`,

  // Freight Providers
  FREIGHT_PROVIDERS: '/api/freight-providers',
  FREIGHT_PROVIDER_BY_ID: (id: string) => `/api/freight-providers/${id}`,

  // Incoterm Configs
  INCOTERM_CONFIGS: '/api/incoterm-configs',
  INCOTERM_CONFIG_BY_ID: (id: string) => `/api/incoterm-configs/${id}`,
  INCOTERM_CONFIGS_SEED: '/api/incoterm-configs/seed-defaults',

  // Admin (Super Admin)
  ADMIN_STATS: '/api/admin/stats',
  ADMIN_TENANTS: '/api/admin/tenants',
  // No es /tenants/comparacion a propósito: ahí :id se lo comería.
  ADMIN_COMPARACION: '/api/admin/comparacion',
  // El alta. Antes una empresa solo podía nacer por el registro público.
  ADMIN_TENANT_CREATE: '/api/admin/tenants',
  ADMIN_USER_CREATE: '/api/admin/users',
  ADMIN_TENANT_BY_ID: (id: string) => `/api/admin/tenants/${id}`,
  ADMIN_TENANT_CLONE: (id: string) => `/api/admin/tenants/${id}/clone`,
  ADMIN_TENANT_ARCHIVAR: (id: string) => `/api/admin/tenants/${id}/archivar`,
  ADMIN_TENANT_RESTAURAR: (id: string) => `/api/admin/tenants/${id}/restaurar`,
  // Reglas de la demo (343, tanda 4): rubro y tienda de una empresa en prueba.
  ADMIN_TENANT_DEMO: (id: string) => `/api/admin/tenants/${id}/demo`,
  ADMIN_TENANT_EXTENDER_PRUEBA: (id: string) => `/api/admin/tenants/${id}/prueba/extender`,
  ADMIN_TENANT_DEMO_CAMBIAR_EJE: (id: string) => `/api/admin/tenants/${id}/demo/cambiar-eje`,
  ADMIN_USERS: '/api/admin/users',
  ADMIN_USER_BY_ID: (id: string) => `/api/admin/users/${id}`,
  // Una persona con su empresa y su rastro de auditoría.
  ADMIN_USER_DETAIL: (id: string) => `/api/admin/users/${id}`,
  ADMIN_IMPERSONATE: (userId: string) => `/api/admin/impersonate/${userId}`,
  /** Qué módulos compró una empresa. Lee y escribe el techo. */
  ADMIN_TENANT_MODULES: (id: string) => `/api/admin/tenants/${id}/modules`,
  // Los permisos por rol de OTRA empresa, sin impersonar a nadie.
  ADMIN_TENANT_PERMISSIONS: (id: string) => `/api/admin/tenants/${id}/permissions`,
  ADMIN_TENANT_PERMISSIONS_ROLE: (id: string, role: string) =>
    `/api/admin/tenants/${id}/permissions/${role}`,
  ADMIN_TENANT_PERMISSIONS_RESET: (id: string, role: string) =>
    `/api/admin/tenants/${id}/permissions/reset/${role}`,
  // El padrón global: una fila por persona, con todos sus portales.
  ADMIN_PERSONAS: '/api/admin/personas',
  // Las invitaciones de la demo (ronda Z): los enlaces que se reparten a los comercios.
  ADMIN_INVITACIONES_DEMO: '/api/admin/invitaciones-demo',
  ADMIN_INVITACION_DEMO: (id: string) => `/api/admin/invitaciones-demo/${id}`,
  ADMIN_INVITACION_DEMO_ALTAS: (id: string) => `/api/admin/invitaciones-demo/${id}/altas`,

  // Plans
  PLANS: '/api/plans',
  PLAN_BY_ID: (id: string) => `/api/plans/${id}`,

  // Subscriptions
  SUBSCRIPTIONS: '/api/subscriptions',
  SUBSCRIPTION_BY_ID: (id: string) => `/api/subscriptions/${id}`,
  SUBSCRIPTION_BY_TENANT: (tenantId: string) => `/api/subscriptions/tenant/${tenantId}`,
  SUBSCRIPTION_CANCEL: (id: string) => `/api/subscriptions/${id}/cancel`,

  // Integrations (SAP)
  INTEGRATIONS: '/api/integrations',
  INTEGRATION_BY_ID: (id: string) => `/api/integrations/${id}`,
  INTEGRATION_TEST: (id: string) => `/api/integrations/${id}/test`,
  INTEGRATION_SYNC: (id: string) => `/api/integrations/${id}/sync`,
  INTEGRATION_LOGS: (id: string) => `/api/integrations/${id}/logs`,
  INTEGRATIONS_ALL_LOGS: '/api/integrations/logs/all',

  // Data Import
  DATA_IMPORT_UPLOAD: '/api/data-import/upload',
  DATA_IMPORT_CONFIRM: (id: string) => `/api/data-import/confirm/${id}`,
  DATA_IMPORT_HISTORY: '/api/data-import/history',
  DATA_IMPORT_BY_ID: (id: string) => `/api/data-import/${id}`,
  DATA_IMPORT_TEMPLATE: (type: string) => `/api/data-import/templates/${type}`,
  DATA_IMPORT_API_PRODUCTS: '/api/data-import/api/products',
  DATA_IMPORT_API_STOCK: '/api/data-import/api/stock',
  DATA_IMPORT_API_SALES: '/api/data-import/api/sales',
  DATA_IMPORT_API_COMPETITOR_PRICES: '/api/data-import/api/competitor-prices',
  DATA_IMPORT_FRESHNESS: '/api/data-import/freshness',
  DATA_IMPORT_FRESHNESS_UPDATE: (type: string) => `/api/data-import/freshness/${type}`,

  // Price Explanation
  DYNAMIC_PRICING_PRODUCT_EXPLAIN: (id: string) => `/api/dynamic-pricing/products/${id}/explain`,

  // Analytics — Charts Dashboard
  DYNAMIC_PRICING_PRICE_HISTORY: (id: string) =>
    `/api/dynamic-pricing/products/${id}/price-history`,
  DYNAMIC_PRICING_COMPETITOR_HISTORY: (id: string) =>
    `/api/dynamic-pricing/products/${id}/competitor-history`,
  DYNAMIC_PRICING_ANALYTICS_PRICE_POSITION: '/api/dynamic-pricing/analytics/price-position',
  DYNAMIC_PRICING_ANALYTICS_TOP_CHANGES: '/api/dynamic-pricing/analytics/top-changes',
  DYNAMIC_PRICING_ANALYTICS_MARKET_VOLATILITY: '/api/dynamic-pricing/analytics/market-volatility',
  DYNAMIC_PRICING_ANALYTICS_MARGIN_BY_CATEGORY: '/api/dynamic-pricing/analytics/margin-by-category',
  DYNAMIC_PRICING_ANALYTICS_STORE_VOLATILITY: '/api/dynamic-pricing/analytics/store-volatility',
  DYNAMIC_PRICING_ANALYTICS_COVERAGE_BY_CATEGORY:
    '/api/dynamic-pricing/analytics/coverage-by-category',

  // Audit Export
  AUDIT_EXPORT_CSV: '/api/audit/export/csv',
  AUDIT_EXPORT_EXCEL: '/api/audit/export/excel',
};
